import React, { useState } from 'react';
import { format, isThisYear } from 'date-fns';
import { PhotoIcon } from '@heroicons/react/24/outline';
import {
  Banknote,
  BarChart3,
  Copy,
  ExternalLink,
  ShoppingCart,
  Ticket,
  TrendingUp,
} from 'lucide-react';

import CopyTextWithTooltip from '@dashboard/components/CopyTextWithTooltip';
import Dash from '@dashboard/components/Dash';
import Dialog from '@dashboard/components/Dialog';
import Link from '@dashboard/components/Link';
import PropertyList from '@dashboard/components/PropertyList';
import Spinner from '@dashboard/components/Spinner';
import RecordStatusBadge from '@dashboard/components/StatusBadge';
import { Badge } from '@dashboard/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@dashboard/components/ui/toggle-group';
import { PAGE_TYPES } from '@dashboard/enums/page-types';
import * as KPICard from '@dashboard/scenes/PageOverview/components/KPICard';
import { cn } from '@dashboard/lib/utils';
import { getImageSrcAndSrcSet } from '@dashboard/services/getUrlFromCdn';
import getConversionRate from '@dashboard/services/getConversionRate';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import getProductPriceLabel from '@dashboard/services/translation/getProductPriceLabel';
import { formatEventDate } from '@dashboard/lib/format-date';

import { getPriceHeadline } from '../PricesList/priceHelpers';
import { ORDER_BUMP_COPY } from './offerCopy';
import { useOrderBumpInfo } from './useOrderBumpMutations';

function Image({ media }) {
  const image = media?.[0];

  if (image?.url) {
    const { src, srcSet } = getImageSrcAndSrcSet({ url: image.url, width: 128, height: 128 });

    return (
      <img
        alt=""
        src={src}
        srcSet={srcSet}
        className="size-16 shrink-0 rounded-lg bg-gray-50 dark:bg-card object-cover ring-1 ring-black/5 dark:ring-ring/5"
      />
    );
  }

  return (
    <div className="flex size-16 shrink-0 items-center justify-center rounded-lg border border-gray-100 dark:border-border bg-gray-50 dark:bg-card">
      <PhotoIcon className="w-6 text-gray-200" />
    </div>
  );
}

// The payment detail page's sections: a PropertyList title, an action in its
// right corner, rows under it.
function Section({ title, action, children }) {
  return (
    <section>
      <PropertyList.Title className="min-h-8 items-center">
        {title}
        {action && <div className="ml-auto">{action}</div>}
      </PropertyList.Title>
      {children}
    </section>
  );
}

// The stat cards' box, for the rows and lists: a border, no fill.
function Box({ as: Tag = 'div', className, children }) {
  return (
    <Tag className={cn('rounded border border-gray-200 dark:border-border', className)}>
      {children}
    </Tag>
  );
}

// Rows in a box, at the detail pages' text size; the box carries the side
// padding and the last row drops its rule.
function Rows({ children }) {
  return <Box className="px-4 text-sm [&>*:last-child]:border-b-0">{children}</Box>;
}

// Label left, value right. The label names the row for assistive tech, as the
// stat cards name theirs.
function Row({ label, children }) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex items-start justify-between gap-4 border-b border-gray-100 dark:border-border py-2"
    >
      <div className="shrink-0 font-semibold">{label}</div>
      <PropertyList.ItemValue className="min-w-0 text-right">{children}</PropertyList.ItemValue>
    </div>
  );
}

function Stat({ icon, title, tooltip, note, children }) {
  return (
    <div role="group" aria-label={title} className="*:h-full">
      <KPICard.Container>
        <div className="flex items-start justify-between gap-2">
          <KPICard.Icon icon={icon} />
          {note && (
            <p className="whitespace-nowrap text-xs text-gray-500 dark:text-muted-foreground">
              {note}
            </p>
          )}
        </div>
        <KPICard.Header>
          <KPICard.Title>{title}</KPICard.Title>
          <KPICard.InfoTooltip>{tooltip}</KPICard.InfoTooltip>
        </KPICard.Header>
        <KPICard.Value>{children}</KPICard.Value>
      </KPICard.Container>
    </div>
  );
}

// The corner of a stat card fits a short date; the year only when it differs.
const formatSoldDate = value => {
  const date = new Date(value);
  return format(date, isThisYear(date) ? 'd MMM' : 'd MMM yyyy');
};

function Performance({ stats, currency, copy }) {
  const [scope, setScope] = useState('thisPage');
  const { sales, revenue, payments, refunded, lastSoldAt, salesSinceAdded } = stats[scope];

  return (
    <Section
      title="Performance"
      // Shown on a bump on one page too, so the seller learns it can be shared.
      action={
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={scope}
          onValueChange={value => value && setScope(value)}
          className="rounded-full"
        >
          <ToggleGroupItem value="thisPage" className="text-xs first:rounded-l-full">
            This page
          </ToggleGroupItem>
          <ToggleGroupItem value="allPages" className="text-xs last:rounded-r-full">
            All pages
          </ToggleGroupItem>
        </ToggleGroup>
      }
    >
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {/* The page overview's order: revenue, payments, conversion. */}
        <Stat
          icon={BarChart3}
          title="Revenue"
          tooltip="Before tax, discounts and refunds"
          note={refunded > 0 && `${refunded} refunded`}
        >
          {getPriceLabel({ amount: revenue, currency })}
        </Stat>
        <Stat
          icon={Banknote}
          title="Sales"
          tooltip={`Payments that included this ${copy.noun}`}
          note={lastSoldAt && `Last sold ${formatSoldDate(lastSoldAt)}`}
        >
          {sales}
        </Stat>
        {/* Nothing records a bump being shown, only bought, so conversion is
            measured against the payments that could have included it. */}
        <Stat
          icon={TrendingUp}
          title="Conversion rate"
          tooltip={`Share of payments that included this ${copy.noun} since it was added`}
        >
          {getConversionRate(salesSinceAdded, payments)}%
        </Stat>
      </div>
    </Section>
  );
}

function StatusBadge({ entry }) {
  if (entry.unavailableReason) {
    return <Badge variant="warning">Unavailable</Badge>;
  }
  if (entry.enabled === false) {
    return <Badge variant="secondary">Disabled</Badge>;
  }
  return <Badge variant="success">Enabled</Badge>;
}

// Product-wide, unlike the placement settings below it: the same on every page.
function ProductDetails({ product }) {
  let stock = 'Unlimited';
  if (product.stock === 0) {
    stock = <RecordStatusBadge status="sold_out" label="Sold out" className="text-xs" />;
  } else if (product.stock != null) {
    stock = (
      <span className={cn(product.stock <= 5 && 'text-amber-700 dark:text-amber-400')}>
        {product.stock} left
      </span>
    );
  }

  return (
    <Section title="Product">
      <Rows>
        <Row label="Stock">{stock}</Row>
        <Row label="SKU">{product.sku ?? <Dash />}</Row>
        <Row label="Price reference">
          {product.priceReference ? (
            // The reference is what a ?show_order_bumps= link names, so it is
            // there to be copied.
            <span className="flex items-center justify-end gap-2">
              <code className="truncate font-mono text-xs">{product.priceReference}</code>
              {/* The icon alone is the trigger, so the tooltip sits over it. */}
              <span className="shrink-0">
                <CopyTextWithTooltip text={product.priceReference}>
                  <span className="flex cursor-pointer text-gray-400 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground">
                    <Copy className="size-3.5" />
                    <span className="sr-only">Copy price reference</span>
                  </span>
                </CopyTextWithTooltip>
              </span>
            </span>
          ) : (
            <Dash />
          )}
        </Row>
      </Rows>
    </Section>
  );
}

const onOff = on => (on ? 'On' : 'Off');

function Settings({ entry, ruleDescription }) {
  const isConditional = entry.showHideLogic?.enabled === true;

  return (
    <Section title="On this page">
      <Rows>
        <Row label="Visibility">
          {entry.hidden ? 'Hidden until the link reveals it' : 'Visible'}
        </Row>
        <Row label="Conditional logic">{isConditional ? ruleDescription : onOff(false)}</Row>
        <Row label="Preselected">{onOff(entry.preselected)}</Row>
        <Row label="Quantity selector">{onOff(entry.allowQuantity)}</Row>
        <Row label="Shorten description">{onOff(entry.shortenDescription)}</Row>
      </Rows>
    </Section>
  );
}

// A checkout reads by its price, an event by its dates, as in their own lists.
function pageDetail(page) {
  if (page.type === PAGE_TYPES.EVENT) {
    const { startDate, endDate, timezone } = page.eventDetails ?? {};
    return startDate || endDate
      ? formatEventDate({ startDate, endDate, timezone }).join(' - ')
      : null;
  }
  return page.product ? getProductPriceLabel(page.product) : null;
}

function PageThumbnail({ url, icon: Icon }) {
  if (url) {
    const { src, srcSet } = getImageSrcAndSrcSet({ url, width: 80, height: 80 });
    return (
      <img
        alt=""
        src={src}
        srcSet={srcSet}
        className="size-10 shrink-0 rounded-md bg-gray-50 dark:bg-card object-cover ring-1 ring-black/5 dark:ring-ring/5"
      />
    );
  }

  return (
    <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-gray-100 dark:bg-muted text-gray-500 dark:text-muted-foreground">
      <Icon className="size-4" />
    </span>
  );
}

function UsedOn({ pages, currentPageId }) {
  return (
    <Section title={pages.length === 1 ? 'Used on 1 page' : `Used on ${pages.length} pages`}>
      <Box as="ul" className="max-h-72 divide-y divide-gray-100 dark:divide-border overflow-y-auto">
        {pages.map(page => {
          const isCurrent = page._id === currentPageId;
          const isEvent = page.type === PAGE_TYPES.EVENT;
          const TypeIcon = isEvent ? Ticket : ShoppingCart;

          const content = (
            <>
              <PageThumbnail url={page.imageUrl} icon={TypeIcon} />
              <span className="flex min-w-0 flex-1 flex-col leading-tight">
                <span className="truncate font-medium text-gray-900 dark:text-foreground">
                  {page.name}
                </span>
                <span className="truncate text-xs font-normal text-gray-500 dark:text-muted-foreground">
                  {[pageDetail(page), isEvent ? 'Event' : 'Checkout'].filter(Boolean).join(' · ')}
                </span>
              </span>
              {isCurrent ? (
                <Badge variant="secondary">Current</Badge>
              ) : (
                <ExternalLink className="size-4 shrink-0 text-gray-400 dark:text-muted-foreground group-hover:text-gray-900 dark:group-hover:text-foreground" />
              )}
            </>
          );

          return (
            <li key={page._id}>
              {isCurrent ? (
                <div className="flex items-center gap-3 px-4 py-2 text-sm">{content}</div>
              ) : (
                // A new tab, so the seller keeps the editor and this dialog open.
                <Link
                  to={`/pages/${page._id}/customize`}
                  aria-label={page.name}
                  bare
                  className="group flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-muted"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {content}
                </Link>
              )}
            </li>
          );
        })}
      </Box>
    </Section>
  );
}

export default function OrderBumpInfoDialog({
  open,
  onClose,
  pageId,
  entry,
  ruleDescription,
  copy = ORDER_BUMP_COPY,
}) {
  const { data, isPending, isError } = useOrderBumpInfo(pageId, entry.productId, {
    enabled: open && Boolean(pageId),
  });
  const { product, price } = entry;
  const currency = price?.currency ?? product?.currency;

  return (
    <Dialog
      open={open}
      onCancel={onClose}
      title={`${copy.Noun} details`}
      size="xl"
      cancelButton={null}
      confirmButton={null}
    >
      {isPending && (
        <div className="flex justify-center py-16" role="status" aria-label="Loading">
          <Spinner />
        </div>
      )}

      {isError && (
        <p className="py-8 text-center text-sm text-gray-500 dark:text-muted-foreground">
          Unable to load this {copy.noun}. Please try again.
        </p>
      )}

      {data && (
        <div className="space-y-6">
          <Box className="flex gap-4 p-3">
            <Image media={product?.media} />
            <div className="flex min-w-0 flex-1 flex-col justify-center">
              <div className="flex items-start justify-between gap-3">
                <p className="line-clamp-2 min-w-0 break-words font-semibold text-gray-900 dark:text-foreground">
                  {product?.title}
                </p>
                <span className="shrink-0">
                  <StatusBadge entry={entry} />
                </span>
              </div>
              {data.product.shortDescription && (
                <p className="line-clamp-2 text-sm text-gray-500 dark:text-muted-foreground">
                  {data.product.shortDescription}
                </p>
              )}
              <p className="text-sm">
                {price ? (
                  <span className="font-medium text-gray-900 dark:text-foreground">
                    {getPriceHeadline(price, { amountsInCents: true })}
                  </span>
                ) : (
                  <span className="text-gray-500 dark:text-muted-foreground">
                    {entry.unavailableReason}
                  </span>
                )}
              </p>
            </div>
          </Box>

          <Performance stats={data.stats} currency={currency} copy={copy} />
          <ProductDetails product={data.product} />
          <Settings entry={entry} ruleDescription={ruleDescription} />
          <UsedOn pages={data.pages} currentPageId={pageId} />
        </div>
      )}
    </Dialog>
  );
}
